import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useRef, useState } from "react";
import {
    KeyboardAvoidingView,
    Platform,
    SafeAreaView,
    ScrollView,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { ActivityIndicator, Snackbar, Text } from "react-native-paper";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { IcdChipPicker } from "@/components/medications/IcdChipPicker";
import { StrengthQuestion } from "@/components/medications/StrengthQuestion";
import { SuggestionQuestion, SUPPLEMENT_ROUTES } from "@/components/medications/SuggestionQuestion";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useMedicationFieldSuggestions } from "@/hooks/useMedicationFieldSuggestions";
import { fetchMedications, updateMedication, deleteMedication, type Medication } from "@/lib/api";
import { isPrnFrequency, mddChipSuggestions } from "@/lib/mdd-utils";
import { STANDARD_FREQUENCIES, isStandardFrequency } from "@/lib/medication-utils";
import { fonts } from "@/lib/theme";

interface IcdCode {
    code: string;
    description: string;
}

export default function EditScreen() {
    const { id: visitId, medicationId } = useLocalSearchParams<{
        id: string;
        medicationId: string;
    }>();
    const router = useRouter();

    // Prevent double-navigation from snackbar timer + manual back press
    const hasNavigatedRef = useRef(false);
    const navTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    useEffect(() => {
        return () => {
            if (navTimerRef.current) {
                clearTimeout(navTimerRef.current);
            }
        };
    }, []);

    const [medication, setMedication] = useState<Medication | null>(null);
    const [loading, setLoading] = useState(true);

    // Editable fields
    const [frequency, setFrequency] = useState<string | null>(null);
    const [customFreq, setCustomFreq] = useState("");
    const [showCustomFreq, setShowCustomFreq] = useState(false);
    const [adminNotes, setAdminNotes] = useState("");
    // PRN: the dose field IS the max daily dose (owner decision). Same
    // chips + Other pattern as the frequency field and the draft card.
    const [mddChip, setMddChip] = useState<string | null>(null);
    const [mddDose, setMddDose] = useState("");
    const [showCustomMdd, setShowCustomMdd] = useState(false);
    const [mddEdited, setMddEdited] = useState(false);
    const [orderedDose, setOrderedDose] = useState("");
    const [route, setRoute] = useState("");
    const [strengthNotApplicable, setStrengthNotApplicable] = useState(false);
    const [suppDose, setSuppDose] = useState("");
    const [selectedIcds, setSelectedIcds] = useState<Set<string>>(new Set());
    const [addedIcds, setAddedIcds] = useState<IcdCode[]>([]);
    const [saving, setSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [snackbar, setSnackbar] = useState<string | null>(null);
    const [dirty, setDirty] = useState(false);
    const { dialogState, showDialog, dismissDialog } = useConfirmDialog();

    // Load medication
    useEffect(() => {
        if (!visitId || !medicationId) {
            return;
        }
        fetchMedications(visitId).then((meds) => {
            const med = meds.find((m) => String(m.id) === medicationId);
            if (med) {
                setMedication(med);
                if (med.frequency && !isStandardFrequency(med.frequency)) {
                    setShowCustomFreq(true);
                    setCustomFreq(med.frequency);
                    setFrequency(null);
                } else {
                    setFrequency(med.frequency);
                }
                setAdminNotes(med.administrationNotes ?? "");
                // Seed from the med's OWN stored ceiling (never its dose).
                if (med.maxDailyDose) {
                    setMddChip(med.maxDailyDose);
                }
                setOrderedDose(med.orderedDose ?? "");
                setRoute(med.route ?? "");
                setStrengthNotApplicable(med.strengthNotApplicable ?? false);
                setSuppDose(med.strength ?? med.dosage ?? "");
                setSelectedIcds(new Set(med.diagnosisCodes.map((d) => d.icdCode)));
                setAddedIcds(
                    med.diagnosisCodes.map((d) => ({
                        code: d.icdCode,
                        description: d.description ?? ""
                    }))
                );
            }
            setLoading(false);
        });
    }, [visitId, medicationId]);

    const supplementSuggestions = useMedicationFieldSuggestions(
        visitId,
        medication?.medicationName ?? "",
        ["doses"],
        medication?.isSupplement === true
    );

    if (loading || !medication) {
        return (
            <SafeAreaView style={styles.safe}>
                <View style={styles.center}>
                    <ActivityIndicator size="large" />
                </View>
            </SafeAreaView>
        );
    }

    const isSupplement = medication.isSupplement;
    const allIcdCodes = addedIcds;
    const effectiveFreq = (showCustomFreq ? customFreq : frequency)?.trim();

    const askMdd = isPrnFrequency(effectiveFreq);
    // Chips: the med's stored ceiling first, then the schedule-derived value
    // (computed from the per-administration ordered dose). Never the dose itself.
    const mddSuggestions = askMdd ? mddChipSuggestions(medication.orderedDose, effectiveFreq) : [];
    const mddExplainByValue = new Map(mddSuggestions.map((sg) => [sg.value, sg.explain]));
    const mddChipOptions = [medication.maxDailyDose, ...mddSuggestions.map((sg) => sg.value)]
        .filter((v): v is string => !!v && !!v.trim())
        .filter((v, i, arr) => arr.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i);
    const effectiveMdd = showCustomMdd ? mddDose.trim() || null : mddChip;

    const handleBack = () => {
        if (dirty) {
            showDialog(
                "Unsaved Changes",
                "You have unsaved changes. Are you sure you want to go back? Your changes will be discarded.",
                [
                    { text: "No, Stay Here", style: "cancel" },
                    {
                        text: "Yes, Go Back & Discard",
                        style: "destructive",
                        onPress: () => router.back()
                    }
                ]
            );
        } else {
            router.back();
        }
    };

    const handleSave = async () => {
        if (
            !effectiveFreq ||
            (isSupplement
                ? (!suppDose.trim() && !strengthNotApplicable) ||
                  !orderedDose.trim() ||
                  !route.trim()
                : selectedIcds.size === 0)
        ) {
            setShowErrors(true);
            return;
        }

        setSaving(true);
        try {
            const codes = allIcdCodes.filter((c) => selectedIcds.has(c.code));
            await updateMedication(visitId!, medicationId!, {
                strength: isSupplement
                    ? strengthNotApplicable
                        ? undefined
                        : suppDose.trim()
                    : undefined,
                strengthNotApplicable: isSupplement ? strengthNotApplicable : undefined,
                orderedDose: isSupplement ? orderedDose.trim() : undefined,
                route: isSupplement ? route.trim() : undefined,
                frequency: effectiveFreq || undefined,
                // Custom wording is stored as typed; a chip is folded onto the
                // vocabulary. Without this the server cannot tell them apart.
                frequencyIsCustom: showCustomFreq,
                // "" clears the stored ceiling (the API folds empty to null);
                // undefined leaves it untouched.
                maxDailyDose: askMdd && mddEdited ? (effectiveMdd ?? "") : undefined,
                administrationNotes: adminNotes || undefined,
                diagnosisCodes: codes.map((c) => ({
                    icdCode: c.code,
                    description: c.description
                }))
            });

            setSnackbar(`${medication.medicationName} updated`);
            navTimerRef.current = setTimeout(() => {
                if (!hasNavigatedRef.current) {
                    hasNavigatedRef.current = true;
                    router.back();
                }
            }, 800);
        } catch {
            showDialog("Error", "Failed to update medication. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        const icdList = medication.diagnosisCodes;
        const bodyText =
            icdList.length > 0
                ? `Removing ${medication.medicationName} will also remove the following associated diagnosis codes:`
                : `Are you sure you want to remove ${medication.medicationName}?`;

        showDialog(
            `Remove ${isSupplement ? "Supplement" : "Medication"}`,
            bodyText +
                (icdList.length > 0
                    ? "\n\n" +
                      icdList.map((d) => `${d.icdCode} — ${d.description}`).join("\n") +
                      "\n\nThis cannot be undone."
                    : "\n\nThis cannot be undone."),
            [
                { text: "Keep", style: "cancel" },
                {
                    text: "Remove",
                    style: "destructive",
                    onPress: async () => {
                        try {
                            await deleteMedication(visitId!, medicationId!, true);
                            router.back();
                        } catch {
                            showDialog(
                                `Could Not Remove ${isSupplement ? "Supplement" : "Medication"}`,
                                `We could not remove ${medication.medicationName}. Please try again.`
                            );
                        }
                    }
                }
            ]
        );
    };

    return (
        <SafeAreaView style={styles.safe}>
            {/* Header */}
            <View style={styles.header}>
                <TouchableOpacity
                    onPress={handleBack}
                    style={styles.backRow}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 20 }}
                    activeOpacity={0.6}
                >
                    <MaterialCommunityIcons name="chevron-left" size={22} color="#45D2B0" />
                    <Text style={styles.backText}>Back</Text>
                </TouchableOpacity>
                <Text style={styles.headerTitle}>
                    Edit {isSupplement ? "Supplement" : "Medication"}
                </Text>
            </View>

            <KeyboardAvoidingView
                style={styles.flexFill}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
            >
                <ScrollView
                    style={styles.scrollContent}
                    keyboardShouldPersistTaps="handled"
                    automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
                >
                    {/* Summary pill */}
                    <View style={styles.summaryPill}>
                        <Text style={styles.summaryName}>{medication.medicationName}</Text>
                        {(medication.strength ?? medication.dosage) && (
                            <View style={styles.doseBadge}>
                                <Text style={styles.doseBadgeText}>
                                    {medication.strength ?? medication.dosage}
                                </Text>
                            </View>
                        )}
                        {!isSupplement && medication.orderedDose && (
                            <View style={styles.doseBadge}>
                                <Text style={styles.doseBadgeText}>{medication.orderedDose}</Text>
                            </View>
                        )}
                        {isSupplement && (
                            <View style={styles.suppBadge}>
                                <Text style={styles.suppBadgeText}>Supplement</Text>
                            </View>
                        )}
                    </View>

                    {isSupplement && (
                        <View style={styles.fieldGroup}>
                            <StrengthQuestion
                                visitId={visitId}
                                productName={medication.medicationName}
                                value={suppDose}
                                onChange={(value) => {
                                    setSuppDose(value);
                                    setDirty(true);
                                }}
                                notApplicable={strengthNotApplicable}
                                onNotApplicableChange={(value) => {
                                    setStrengthNotApplicable(value);
                                    setDirty(true);
                                }}
                                showError={showErrors}
                            />
                            <SuggestionQuestion
                                label="Amount taken"
                                required
                                value={orderedDose}
                                onChange={(value) => {
                                    setOrderedDose(value);
                                    setDirty(true);
                                }}
                                options={supplementSuggestions.suggestions?.doses ?? []}
                                loading={supplementSuggestions.loading}
                                showError={showErrors}
                                placeholder="For example, 2 tablets"
                            />
                            <SuggestionQuestion
                                label="Route"
                                required
                                value={route}
                                onChange={(value) => {
                                    setRoute(value);
                                    setDirty(true);
                                }}
                                options={SUPPLEMENT_ROUTES}
                                showError={showErrors}
                                placeholder="Enter route"
                            />
                        </View>
                    )}

                    {/* Frequency */}
                    <View style={styles.fieldGroup}>
                        <Text
                            style={[
                                styles.fieldLabel,
                                showErrors && !effectiveFreq && { color: "#DC2626" }
                            ]}
                        >
                            Frequency *
                        </Text>
                        <View style={styles.chipGroup}>
                            {STANDARD_FREQUENCIES.map((freq) => (
                                <TouchableOpacity
                                    key={freq}
                                    style={[
                                        styles.chip,
                                        frequency === freq && !showCustomFreq && styles.chipSelected
                                    ]}
                                    onPress={() => {
                                        setFrequency(freq);
                                        setShowCustomFreq(false);
                                        setDirty(true);
                                    }}
                                >
                                    <Text
                                        style={[
                                            styles.chipText,
                                            frequency === freq &&
                                                !showCustomFreq &&
                                                styles.chipTextSelected
                                        ]}
                                    >
                                        {freq}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                            <TouchableOpacity
                                style={[
                                    styles.chip,
                                    styles.chipDashed,
                                    showCustomFreq && styles.chipSelected
                                ]}
                                onPress={() => {
                                    setShowCustomFreq(true);
                                    setFrequency(null);
                                    setDirty(true);
                                }}
                            >
                                <Text
                                    style={[
                                        styles.chipText,
                                        showCustomFreq && styles.chipTextSelected
                                    ]}
                                >
                                    Other
                                </Text>
                            </TouchableOpacity>
                        </View>
                        {showCustomFreq && (
                            <TextInput
                                style={styles.textInput}
                                placeholder="Enter custom frequency..."
                                placeholderTextColor="#B0AECE"
                                value={customFreq}
                                onChangeText={(t) => {
                                    setCustomFreq(t);
                                    setDirty(true);
                                }}
                                autoFocus
                                autoCorrect={false}
                                spellCheck={false}
                            />
                        )}
                        {showErrors && !effectiveFreq && (
                            <Text style={styles.fieldError}>Please select a frequency</Text>
                        )}
                    </View>

                    {/* Max daily dose — its own field for PRN meds */}
                    {askMdd && !isSupplement && (
                        <View style={[styles.fieldGroup, styles.mddCard]}>
                            <View style={styles.mddHeaderRow}>
                                <Text style={styles.mddLabel}>Maximum Daily Dose</Text>
                                <View style={styles.prnBadge}>
                                    <Text style={styles.prnBadgeText}>PRN</Text>
                                </View>
                            </View>
                            <Text style={styles.mddHint}>
                                The most the patient may take in 24 hours.
                            </Text>
                            <View style={styles.chipGroup}>
                                {mddChipOptions.map((opt) => (
                                    <TouchableOpacity
                                        key={opt}
                                        style={[
                                            styles.chip,
                                            mddChip === opt && !showCustomMdd && styles.chipSelected
                                        ]}
                                        onPress={() => {
                                            setMddChip(
                                                !showCustomMdd && mddChip === opt ? null : opt
                                            );
                                            setShowCustomMdd(false);
                                            setMddEdited(true);
                                            setDirty(true);
                                        }}
                                    >
                                        <Text
                                            style={[
                                                styles.chipText,
                                                mddChip === opt &&
                                                    !showCustomMdd &&
                                                    styles.chipTextSelected
                                            ]}
                                        >
                                            {opt}
                                            {mddExplainByValue.has(opt)
                                                ? ` (${mddExplainByValue.get(opt)})`
                                                : ""}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                                <TouchableOpacity
                                    style={[
                                        styles.chip,
                                        styles.chipDashed,
                                        showCustomMdd && styles.chipSelected
                                    ]}
                                    onPress={() => {
                                        setShowCustomMdd(true);
                                        setMddChip(null);
                                        setMddEdited(true);
                                        setDirty(true);
                                    }}
                                >
                                    <Text
                                        style={[
                                            styles.chipText,
                                            showCustomMdd && styles.chipTextSelected
                                        ]}
                                    >
                                        Other
                                    </Text>
                                </TouchableOpacity>
                            </View>
                            {showCustomMdd && (
                                <TextInput
                                    style={styles.textInput}
                                    placeholder="e.g., 8 tablets"
                                    placeholderTextColor="#B0AECE"
                                    value={mddDose}
                                    onChangeText={(t) => {
                                        setMddDose(t);
                                        setMddEdited(true);
                                        setDirty(true);
                                    }}
                                    autoCorrect={false}
                                    spellCheck={false}
                                />
                            )}
                        </View>
                    )}

                    {/* Administration Notes */}
                    <View style={styles.fieldGroup}>
                        <Text style={styles.fieldLabel}>Administration Notes</Text>
                        <TextInput
                            style={[styles.textInput, { minHeight: 60, textAlignVertical: "top" }]}
                            placeholder="Enter notes..."
                            placeholderTextColor="#B0AECE"
                            value={adminNotes}
                            onChangeText={(t) => {
                                setAdminNotes(t);
                                setDirty(true);
                            }}
                            autoCorrect={false}
                            spellCheck={false}
                            multiline
                        />
                    </View>

                    {/* Diagnosis Codes */}
                    <View style={styles.fieldGroup}>
                        <Text
                            style={[
                                styles.fieldLabel,
                                showErrors &&
                                    selectedIcds.size === 0 &&
                                    !isSupplement && { color: "#DC2626" }
                            ]}
                        >
                            Diagnosis Codes {!isSupplement && "*"}
                        </Text>
                        <IcdChipPicker
                            suggestedCodes={allIcdCodes}
                            selectedCodes={selectedIcds}
                            onToggle={(code) => {
                                setSelectedIcds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(code)) {
                                        next.delete(code);
                                    } else {
                                        next.add(code);
                                    }
                                    return next;
                                });
                                setDirty(true);
                            }}
                            onAdd={(code) => {
                                setAddedIcds((prev) => [...prev, code]);
                                setSelectedIcds((prev) => new Set([...prev, code.code]));
                                setDirty(true);
                            }}
                        />
                        {showErrors && selectedIcds.size === 0 && !isSupplement && (
                            <Text style={styles.fieldError}>
                                Please select at least one diagnosis code
                            </Text>
                        )}
                    </View>

                    {/* Delete button */}
                    <TouchableOpacity style={styles.deleteBtn} onPress={handleDelete}>
                        <Text style={styles.deleteBtnText}>
                            Delete {isSupplement ? "Supplement" : "Medication"}
                        </Text>
                    </TouchableOpacity>
                </ScrollView>
            </KeyboardAvoidingView>

            {/* Sticky save */}
            <View style={styles.stickyBottom}>
                <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving}>
                    {saving ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                        <Text style={styles.saveBtnText}>Save Changes</Text>
                    )}
                </TouchableOpacity>
            </View>

            <Snackbar
                visible={snackbar !== null}
                onDismiss={() => setSnackbar(null)}
                duration={1500}
                style={styles.snackbar}
            >
                {snackbar ? `\u2713 ${snackbar}` : ""}
            </Snackbar>
            <ConfirmDialog {...dialogState} onDismiss={dismissDialog} />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: "#FAFBFF" },
    flexFill: { flex: 1 },
    center: { flex: 1, alignItems: "center", justifyContent: "center" },
    header: {
        paddingHorizontal: 20,
        paddingTop: 8,
        paddingBottom: 16,
        backgroundColor: "#FFFFFF",
        borderBottomWidth: 1,
        borderBottomColor: "#F0ECF3"
    },
    backRow: {
        flexDirection: "row" as const,
        alignItems: "center" as const,
        marginLeft: -4,
        marginBottom: 8
    },
    backText: { fontSize: 14, fontFamily: fonts.semiBold, color: "#45D2B0" },
    headerTitle: { fontSize: 22, fontFamily: fonts.extraBold, color: "#1F1547" },
    scrollContent: { flex: 1, padding: 20 },
    summaryPill: {
        backgroundColor: "#FAFBFF",
        borderWidth: 1.5,
        borderColor: "#F0ECF3",
        borderRadius: 18,
        padding: 14,
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: 8,
        marginBottom: 20,
        flexWrap: "wrap" as const
    },
    summaryName: { fontSize: 15, fontFamily: fonts.bold, color: "#1F1547" },
    doseBadge: {
        backgroundColor: "#EDE9FE",
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 6
    },
    doseBadgeText: { fontSize: 11, fontFamily: fonts.semiBold, color: "#7C3AED" },
    suppBadge: {
        backgroundColor: "#FFFBEB",
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 9999
    },
    suppBadgeText: {
        fontSize: 9,
        fontFamily: fonts.bold,
        color: "#B45309",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5
    },
    fieldGroup: { marginBottom: 18 },
    mddCard: {
        backgroundColor: "#FFFBEB",
        borderWidth: 1.5,
        borderColor: "#FDE68A",
        borderRadius: 14,
        padding: 12
    },
    mddHeaderRow: { flexDirection: "row" as const, alignItems: "center" as const, gap: 8 },
    mddLabel: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: "#B45309",
        textTransform: "uppercase" as const,
        letterSpacing: 0.8
    },
    prnBadge: {
        backgroundColor: "#F59E0B",
        paddingHorizontal: 7,
        paddingVertical: 1,
        borderRadius: 9999
    },
    prnBadgeText: {
        fontSize: 9,
        fontFamily: fonts.bold,
        color: "#FFFFFF",
        letterSpacing: 0.5
    },
    mddHint: { fontSize: 11, fontFamily: fonts.regular, color: "#92700E", marginTop: 4 },
    mddSuggestBtn: {
        alignSelf: "flex-start" as const,
        backgroundColor: "#FFFFFF",
        borderWidth: 1.5,
        borderColor: "#F59E0B",
        borderRadius: 9999,
        paddingHorizontal: 12,
        paddingVertical: 6,
        marginTop: 8
    },
    mddSuggestBtnText: { fontSize: 12, fontFamily: fonts.bold, color: "#B45309" },
    fieldLabel: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: "#B0AECE",
        textTransform: "uppercase" as const,
        letterSpacing: 0.8,
        marginBottom: 4
    },
    fieldError: { fontSize: 11, color: "#EF4444", fontFamily: fonts.semiBold, marginTop: 4 },
    chipGroup: { flexDirection: "row" as const, flexWrap: "wrap" as const, gap: 5, marginTop: 4 },
    chip: {
        paddingHorizontal: 11,
        paddingVertical: 5,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: "#EDEAF2",
        backgroundColor: "#FFFFFF"
    },
    chipSelected: { borderColor: "#45D2B0", backgroundColor: "#45D2B0" },
    chipDashed: { borderStyle: "dashed" as const, borderColor: "#DDD8E6" },
    chipText: { fontSize: 11, fontFamily: fonts.semiBold, color: "#4A4A68" },
    chipTextSelected: { color: "#FFFFFF" },
    textInput: {
        borderWidth: 1.5,
        borderColor: "#EDEAF2",
        borderRadius: 10,
        padding: 10,
        fontSize: 13,
        fontFamily: fonts.regular,
        color: "#1F1547",
        backgroundColor: "#FFFFFF",
        marginTop: 8
    },
    deleteBtn: {
        alignItems: "center" as const,
        paddingVertical: 12,
        marginTop: 14,
        marginBottom: 40
    },
    deleteBtnText: { fontSize: 14, fontFamily: fonts.bold, color: "#EF4444" },
    stickyBottom: {
        paddingHorizontal: 20,
        paddingTop: 12,
        paddingBottom: 28,
        backgroundColor: "#FFFFFF",
        borderTopWidth: 1,
        borderTopColor: "#F0ECF3"
    },
    saveBtn: {
        backgroundColor: "#45D2B0",
        paddingVertical: 14,
        borderRadius: 9999,
        alignItems: "center" as const
    },
    saveBtnText: { fontSize: 15, fontFamily: fonts.bold, color: "#FFFFFF" },
    snackbar: { backgroundColor: "#1F1547" }
});
